import { Position } from "@xyflow/react";

import { HANDLE_SIZE } from "../nodes/styled/basehandle.styled";

const DEFAULT_CURVATURE = 0.25;

/**
 * Decides how far the curve should “reach” along the connection direction from one handle.
 * If the other end is roughly in front of this handle, we use half the gap between the two
 * points—simple and predictable. If the other end is more behind or to the side (the tricky
 * case), we use a gentler, capped stretch so the line doesn’t shoot off wildly.
 *
 * @param distance Whether the other point is mostly in front of this handle (positive) or
 *   more behind it along the arrow direction (negative), measured along that direction.
 * @param curvature How strong that gentler stretch is when the other point isn’t straight ahead.
 * @returns How many pixels to offset the shaping point along the handle line.
 */
function calculateControlOffset(distance: number, curvature: number) {
	if (distance >= 0) {
		return 0.5 * distance;
	}
	return curvature * 25 * Math.sqrt(-distance);
}

/**
 * Finds one of the two invisible “bend handles” React Flow uses to draw a smooth edge. It sits
 * on the same side of the node as the real handle (top, bottom, left, or right) and is pushed
 * outward by {@link calculateControlOffset} so the arc looks like XYFlow’s built-in Bézier edge.
 *
 * @param pos Which edge of the node the connection leaves from or arrives at.
 * @param x1 X of the endpoint we’re shaping (source or target).
 * @param y1 Y of that endpoint.
 * @param x2 X of the opposite endpoint (so we know which way the line should lean).
 * @param y2 Y of the opposite endpoint.
 * @param c Curvature strength; forwarded to {@link calculateControlOffset}.
 * @returns The `[x, y]` of this side’s bend point.
 */
function getControlWithCurvature({
	pos,
	x1,
	y1,
	x2,
	y2,
	c,
}: {
  pos: Position;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  c: number;
}): [number, number] {
	switch (pos) {
		case Position.Left:
			return [x1 - calculateControlOffset(x1 - x2, c), y1];
		case Position.Right:
			return [x1 + calculateControlOffset(x2 - x1, c), y1];
		case Position.Top:
			return [x1, y1 - calculateControlOffset(y1 - y2, c)];
		case Position.Bottom:
			return [x1, y1 + calculateControlOffset(y2 - y1, c)];
		default:
			return [x1, y1];
	}
}

/** Everything needed to describe the same line XYFlow would draw: both ends, which side each handle is on, and how curvy the arc is. */
export type BezierEdgeEndpoints = {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  sourcePosition?: Position;
  targetPosition?: Position;
  curvature?: number;
};

/**
 * Picks a single point along the curved connector—the same shape `@xyflow/react`’s
 * {@link getBezierPath} would produce from these inputs. Handy for centering a label on the wire,
 * drawing an arrowhead mid-path, or checking clicks along the line instead of a straight chord.
 *
 * @param t How far along the line from start to finish: `0` is the source, `1` is the target.
 *   Numbers below 0 or above 1 keep going along the same mathematical curve (outside the visible segment).
 * @param params Both endpoints, which side each handle uses (defaults: source bottom, target top),
 *   and optional `curvature` (defaults to `0.25`, same as XYFlow’s Bézier edge).
 * @returns The `[x, y]` of that spot in the same pixels as your node positions.
 */
export function getBezierPointAtT(t: number, params: BezierEdgeEndpoints): [number, number] {
	const c = params.curvature ?? DEFAULT_CURVATURE;
	const [sourceControlX, sourceControlY] = getControlWithCurvature({
		pos: params.sourcePosition ?? Position.Bottom,
		x1: params.sourceX,
		y1: params.sourceY,
		x2: params.targetX,
		y2: params.targetY,
		c,
	});
	const [targetControlX, targetControlY] = getControlWithCurvature({
		pos: params.targetPosition ?? Position.Top,
		x1: params.targetX,
		y1: params.targetY,
		x2: params.sourceX,
		y2: params.sourceY,
		c,
	});
	const mt = 1 - t;
	const mt2 = mt * mt;
	const mt3 = mt2 * mt;
	const t2 = t * t;
	const t3 = t2 * t;
	const x =
    mt3 * params.sourceX +
    3 * mt2 * t * sourceControlX +
    3 * mt * t2 * targetControlX +
    t3 * params.targetX;
	const y =
    mt3 * params.sourceY +
    3 * mt2 * t * sourceControlY +
    3 * mt * t2 * targetControlY +
    t3 * params.targetY;
	return [x, y];
}

type Pt = [number, number];

/**
 * Vertical leg before the return-style sweep. Used only for the For Each self-edge (empty body).
 */
export const FOR_EACH_EMPTY_BODY_DROP = 400;

/**
 * Cubic sweep after the vertical drop on the for-each return edge (`corner` → Loop Start `in`).
 */
export function getForEachReturnBezierPoints(
	sourceX: number,
	sourceY: number,
	targetX: number,
	targetY: number,
): { p0: Pt; p1: Pt; p2: Pt; p3: Pt } | null {
	const dx = targetX - sourceX;
	const dy = targetY - sourceY;
	const dist = Math.hypot(dx, dy);
	if (dist < 0.5) return null;
	const inv = 1 / dist;
	const nx = dy * inv;
	const ny = -dx * inv;
	const b = 0.52 * dist;
	const f = 0.32;
	const c1x = sourceX + dx * f + nx * b;
	const c1y = sourceY + dy * f + ny * b;
	const c2x = targetX - dx * f + nx * b;
	const c2y = targetY - dy * f + ny * b;
	return {
		p0: [sourceX, sourceY],
		p1: [c1x, c1y],
		p2: [c2x, c2y],
		p3: [targetX, targetY],
	};
}

/**
 * One point on a cubic Bézier (P0–P3, where P1/P2 are the usual control points).
 */
export function cubicBezierPoint(
	t: number,
	p0: Pt,
	p1: Pt,
	p2: Pt,
	p3: Pt,
): Pt {
	const m = 1 - t;
	const m2 = m * m;
	const m3 = m2 * m;
	const t2 = t * t;
	const t3 = t2 * t;
	return [
		m3 * p0[0] + 3 * m2 * t * p1[0] + 3 * m * t2 * p2[0] + t3 * p3[0],
		m3 * p0[1] + 3 * m2 * t * p1[1] + 3 * m * t2 * p2[1] + t3 * p3[1],
	];
}

/** “For Each” pill along the vertical drop. */
const FOR_EACH_SELF_LOOP_LABEL_VERTICAL_FRAC = 0.38;
/** Matches pill `HALF_PILL_HEIGHT` in LoopForEachEdge (self-loop path). */
const FOR_EACH_SELF_LOOP_PILL_HALF_H = 12;
/** Clear px between bottom of pill rect and top of the + (EdgeAddButton / AddConnectionHandle chrome). */
const FOR_EACH_SELF_LOOP_ADD_CLEAR_GAP_PX = 50;
const EDGE_ADD_BUTTON_HALF = HANDLE_SIZE / 2;

export function forEachSelfLoopPathD(
	sourceX: number,
	sourceY: number,
	targetX: number,
	targetY: number,
): { d: string; labelX: number; labelY: number; addX: number; addY: number } {
	const cornerX = sourceX;
	const cornerY = sourceY + FOR_EACH_EMPTY_BODY_DROP;
	const labelX = sourceX;
	const labelY = sourceY + FOR_EACH_EMPTY_BODY_DROP * FOR_EACH_SELF_LOOP_LABEL_VERTICAL_FRAC;
	const pillBottomY = labelY + FOR_EACH_SELF_LOOP_PILL_HALF_H;
	/** + is centered on this point; must clear pill + keep whole control on the vertical before the bend. */
	const addX = sourceX;
	const addY = Math.min(
		pillBottomY + FOR_EACH_SELF_LOOP_ADD_CLEAR_GAP_PX + EDGE_ADD_BUTTON_HALF,
		cornerY - EDGE_ADD_BUTTON_HALF - 6,
	);
	const ret = getForEachReturnBezierPoints(cornerX, cornerY, targetX, targetY);
	if (!ret) {
		return {
			d: `M ${sourceX},${sourceY} L ${cornerX},${cornerY} L ${targetX},${targetY}`,
			labelX,
			labelY,
			addX,
			addY,
		};
	}
	const { p1, p2, p3 } = ret;
	const d = `M ${sourceX},${sourceY} L ${cornerX},${cornerY} C ${p1[0]},${p1[1]} ${p2[0]},${p2[1]} ${p3[0]},${p3[1]}`;
	return { d, labelX, labelY, addX, addY };
}

function arcLengthAlongBezier(params: BezierEdgeEndpoints, tStart: number, tEnd: number): number {
	if (tEnd <= tStart) return 0;
	const segments = 36;
	let len = 0;
	let prev = getBezierPointAtT(tStart, params);
	for (let i = 1; i <= segments; i++) {
		const u = tStart + ((tEnd - tStart) * i) / segments;
		const p = getBezierPointAtT(u, params);
		len += Math.hypot(p[0] - prev[0], p[1] - prev[1]);
		prev = p;
	}
	return len;
}

/**
 * Loop Start → first body node: pill at the same early `t` as the empty self-loop’s vertical
 * fraction, + centered after pill half + gap + handle radius along the Bézier (matches For Each self-loop rules).
 */
export function getForEachLoopStartEntryPlacement(
	params: BezierEdgeEndpoints,
): { labelX: number; labelY: number; addX: number; addY: number } {
	const tLabel = FOR_EACH_SELF_LOOP_LABEL_VERTICAL_FRAC;
	const [labelX, labelY] = getBezierPointAtT(tLabel, params);
	const wantOffset =
    FOR_EACH_SELF_LOOP_PILL_HALF_H + FOR_EACH_SELF_LOOP_ADD_CLEAR_GAP_PX + EDGE_ADD_BUTTON_HALF;
	const maxT = 0.97;
	const totalAvail = arcLengthAlongBezier(params, tLabel, maxT);
	const targetLen = Math.min(wantOffset, Math.max(totalAvail, 0));

	if (targetLen < 1e-3) {
		const [addX, addY] = getBezierPointAtT(Math.min(tLabel + 0.08, maxT), params);
		return { labelX, labelY, addX, addY };
	}

	let lo = tLabel;
	let hi = maxT;
	for (let i = 0; i < 28; i++) {
		const mid = (lo + hi) / 2;
		const len = arcLengthAlongBezier(params, tLabel, mid);
		if (len < targetLen) lo = mid;
		else hi = mid;
	}
	const tAdd = (lo + hi) / 2;
	const [addX, addY] = getBezierPointAtT(tAdd, params);
	return { labelX, labelY, addX, addY };
}

/**
 * Flow-space point on the For Each self-loop near the + control, from the Loop Start sentinel
 * node’s top-left position (matches self-loop path used in the canvas).
 */
export function getForEachSelfLoopAddAnchor(nodeTopLeft: { x: number; y: number }): [number, number] {
	const w = 250;
	const sourceX = nodeTopLeft.x + w / 2;
	const sourceY = nodeTopLeft.y + 92;
	const targetX = nodeTopLeft.x + 2;
	const targetY = nodeTopLeft.y + 58;
	const { addX, addY } = forEachSelfLoopPathD(sourceX, sourceY, targetX, targetY);
	return [addX, addY];
}

/**
 * Vertical run (px) below the last body node’s `out` before the return sweep to Loop Start `in`
 * (same order of magnitude as {@link FOR_EACH_EMPTY_BODY_DROP} on the self-loop).
 */
export const FOR_EACH_RETURN_VERTICAL_DROP = 280;

/** + on the straight leg, as a fraction of the drop (0 = last node, 1 = corner before curve). */
const FOR_EACH_RETURN_ADD_ON_STRAIGHT_FRAC = 0.58;

function getForEachReturnCorner(
	sourceX: number,
	sourceY: number,
): { cornerX: number; cornerY: number } {
	return { cornerX: sourceX, cornerY: sourceY + FOR_EACH_RETURN_VERTICAL_DROP };
}

/**
 * Last body node → Loop Start `in`: straight down from `out`, then a smooth sweep to the
 * sentinel (semicircle-style bulge), matching the empty for-each self-loop pattern.
 */
export function getForEachReturnPathD(
	sourceX: number,
	sourceY: number,
	targetX: number,
	targetY: number,
): { d: string } {
	const { cornerX, cornerY } = getForEachReturnCorner(sourceX, sourceY);
	const pts = getForEachReturnBezierPoints(cornerX, cornerY, targetX, targetY);
	if (!pts) {
		return { d: `M ${sourceX},${sourceY} L ${cornerX},${cornerY} L ${targetX},${targetY}` };
	}
	const { p1, p2, p3 } = pts;
	return {
		d: `M ${sourceX},${sourceY} L ${cornerX},${cornerY} C ${p1[0]},${p1[1]} ${p2[0]},${p2[1]} ${p3[0]},${p3[1]}`,
	};
}

/**
 * “Insert after” control on the vertical segment, before the return curve (see {@link getForEachReturnPathD}).
 */
export function getForEachReturnEdgeAddButtonPosition(
	sourceX: number,
	sourceY: number,
	targetX: number,
	targetY: number,
): { x: number; y: number } {
	const { cornerX, cornerY } = getForEachReturnCorner(sourceX, sourceY);
	const leg = cornerY - sourceY;
	if (leg < 1) {
		return { x: (sourceX + targetX) / 2, y: (sourceY + targetY) / 2 };
	}
	const t = FOR_EACH_RETURN_ADD_ON_STRAIGHT_FRAC;
	let y = sourceY + leg * t;
	/** Keep the + on the open straight, not in the fillet at the corner. */
	y = Math.min(y, cornerY - EDGE_ADD_BUTTON_HALF - 8);
	return { x: sourceX, y };
}
